import { initializeApp } from 'firebase/app';
import { getAuth, initializeAuth, indexedDBLocalPersistence, GoogleAuthProvider, OAuthProvider, signInWithCredential, connectAuthEmulator } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { getFirestore, initializeFirestore, persistentLocalCache, persistentMultipleTabManager, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getMessaging, getToken, onMessage, deleteToken } from 'firebase/messaging';
import { hasAnalyticsConsent, ANALYTICS_CONSENT_EVENT } from '../utils/consent';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const requiredKeys = ['apiKey', 'authDomain', 'projectId', 'appId'];
const firebaseReady = requiredKeys.every((key) => Boolean(firebaseConfig[key]));

let app = null;
let analytics = null;
let auth = null;
let googleProvider = null;
let appleProvider = null;
let db = null;
let storage = null;
let functions = null;
let messaging = null;

const clearFirebaseEmulatorDefaults = () => {
  try {
    if (typeof window !== 'undefined' && window.__FIREBASE_DEFAULTS__) {
      delete window.__FIREBASE_DEFAULTS__;
    }
  } catch (e) {
    // no-op
  }

  try {
    if (typeof document !== 'undefined') {
      document.cookie = '__FIREBASE_DEFAULTS__=; Max-Age=0; path=/';
      document.cookie = '__FIREBASE_DEFAULTS__=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    }
  } catch (e) {
    // no-op
  }

  try {
    const emulatorKeys = [
      'firebase:emulator-host:auth',
      'firebase:emulator-host:firestore',
      'firebase:emulator-host:storage',
      'firebase:emulator-host:functions'
    ];
    if (typeof localStorage !== 'undefined') {
      emulatorKeys.forEach((key) => localStorage.removeItem(key));
    }
    if (typeof sessionStorage !== 'undefined') {
      emulatorKeys.forEach((key) => sessionStorage.removeItem(key));
    }
  } catch (e) {
    // no-op
  }
};

if (firebaseReady) {
  clearFirebaseEmulatorDefaults();
  app = initializeApp(firebaseConfig);

  // Native: use indexedDB persistence for reliable auth state in WKWebView
  if (Capacitor.isNativePlatform()) {
    auth = initializeAuth(app, {
      persistence: indexedDBLocalPersistence,
    });
  } else {
    auth = getAuth(app);
  }

  googleProvider = new GoogleAuthProvider();
  appleProvider = new OAuthProvider('apple.com');
  appleProvider.addScope('email');
  appleProvider.addScope('name');
  appleProvider.setCustomParameters({ locale: 'fr' });
  // Cache Firestore persistant (IndexedDB) : l'app reste lisible hors ligne et
  // le profil se charge depuis le cache quand le reseau traine. Multi-onglets
  // pour le web ; repli sur getFirestore si l'API n'est pas disponible
  // (WebView sans IndexedDB, navigation privee).
  try {
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
    });
  } catch (e) {
    console.warn('[Firebase] Cache Firestore persistant indisponible, repli memoire', e?.message || e);
    db = getFirestore(app);
  }
  storage = getStorage(app);
  functions = getFunctions(app, 'europe-west1');

  // ===== SANDBOX EMULATOR BRANCH =====
  // Si VITE_FIREBASE_USE_EMULATORS=true, branche Auth/Firestore/Functions/Storage sur localhost.
  // Storage est aussi sur l'emulator pour empêcher tout write accidentel sur prod
  // (un admin connecté en sandbox pourrait sinon uploader vers la vraie Storage).
  // Conséquence : les médias produits prod ne seront pas chargés en sandbox (404 attendus).
  // À appeler AVANT toute autre opération Firestore/Auth/Storage.
  if (import.meta.env.VITE_FIREBASE_USE_EMULATORS === 'true' && !Capacitor.isNativePlatform()) {
    try {
      connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
      connectFirestoreEmulator(db, '127.0.0.1', 8080);
      connectFunctionsEmulator(functions, '127.0.0.1', 5001);
      connectStorageEmulator(storage, '127.0.0.1', 9199);
      console.log('[Firebase] Emulators connected (Auth/Firestore/Functions/Storage on localhost)');
      console.log('[Firebase] Note: prod media images will 404 in sandbox (expected)');
    } catch (e) {
      console.warn('[Firebase] Emulator connection failed:', e.message);
    }
  }

  // Analytics : jamais en natif, et seulement apres consentement (banniere cookies).
  // Differe pour ne pas peser sur le bundle initial ; s'active aussi a l'acceptation
  // en cours de session via l'evenement emis par utils/consent.js.
  const startAnalytics = async () => {
    if (analytics || Capacitor.isNativePlatform() || !hasAnalyticsConsent()) return;
    try {
      const { getAnalytics } = await import('firebase/analytics');
      analytics = getAnalytics(app);
    } catch (e) {
      console.warn('[Firebase] Analytics indisponible', e?.message || e);
    }
  };
  if (typeof window !== 'undefined') {
    window.addEventListener(ANALYTICS_CONSENT_EVENT, () => { startAnalytics(); });
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => { startAnalytics(); });
    } else {
      setTimeout(() => { startAnalytics(); }, 2000);
    }
  }
  
  // Messaging only supported on Web (native uses @capacitor/push-notifications)
  if (!Capacitor.isNativePlatform()) {
    try {
       messaging = getMessaging(app);
    } catch (e) {
       console.log('Firebase Messaging not supported'); 
    }
  }

} else {
  console.warn('Firebase non configuré: renseigne les variables VITE_FIREBASE_* dans .env.');
}

export { app, analytics, auth, db, storage, functions, messaging, getToken, onMessage, deleteToken, firebaseReady, googleProvider, appleProvider, signInWithCredential, GoogleAuthProvider, OAuthProvider };
